<template>
  <v-card
    class="pa-5 ml-10 mr-10 mt-5"
    :title="t('views.licence.table.title')">
    <template v-slot:append>
      <div class="paymentMode">
        <button @click="mode='monthly'" class="modeButton" :class="mode === 'monthly' ? 'active' : 'not-active'">
          {{ $t("views.licence.other.monthly") }}
        </button>
        <button @click="mode='yearly'" class="modeButton" :class="mode === 'yearly' ? 'active' : 'not-active'">
          {{ $t("views.licence.other.yearly") }}
        </button>
      </div>
    </template>
    <v-card-text>
      <v-data-table
        hide-default-footer
        items-per-page="20"
        class="border-table"
        density="compact"
        :headers="headers"
        hide-no-data
        item-value="id"
        :items="items">
        <template v-slot:item.active="{ item }">
          <v-checkbox
            :disabled="item.requires && (item.requires & tempResult) == 0"
            :data-cy="'module-' + item.id + '-active'"
            :true-value="1"
            :false-value="0"
            :model-value="item.slots"
            @update:model-value="($event) => changeValue(item.value, $event)"
            hide-details>
          </v-checkbox>

          <span v-if="item.monthEnd"> {{$t("views.licence.review.until", {date: (new Date(props.next_billing_cycle_anchor * 1000)).toLocaleDateString()})}}</span>
        </template>
      </v-data-table>
    </v-card-text>
    <v-card-text>
      <v-row class="mt-5">
        <v-col
          cols="6"
          style="display: grid; justify-content: end"
          >{{ $t("views.licence.review." + mode) }}</v-col
        >
        <v-col
          cols="6"
          style="display: grid; justify-content: end">
          <s
            data-cy="old-monthly-costs"
            v-if="reviewData.oldCosts !== 0 && reviewData.oldCosts !== reviewData.newCosts"
            >{{ reviewData.oldCosts }} €</s
          >
          <span data-cy="final-monthly-costs">{{ reviewData.newCosts }} €</span>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn @click="router.back()"> Zurück </v-btn>
      <v-btn
        :readonly="saving"
        @click="saveModules(() => save())">
        <template v-if="saving">
          <v-progress-circular indeterminate />
        </template>
        <template v-else>Speichern</template>
      </v-btn>
    </v-card-actions>
  </v-card>
  <v-dialog
    v-model="dialog.open"
    persistent>
    <LicenceReviewPopup
      private
      @close="dialog.open = false"
      @save="submit"
      :review-data="reviewData"
      :initial-items="initialItems"
      :items="items"
      :charge-needed="chargeNeeded"
      :modules-removed="modulesRemoved" />
  </v-dialog>
</template>

<script lang="ts" setup>
import { useConfirm, useSnackbar } from "vuetify-use-dialog";
import { computed, ModelRef, Ref, ref, toRef } from "vue";
import { useTranslation } from "i18next-vue";
import { useMainStore } from "@/stores/mainStore";
import { useModules } from "./moduleComposable";
import LicenceReviewPopup from "@/components/dialogs/LicenceReviewPopup.vue";
import { c } from "vite/dist/node/types.d-aGj9QkWt";
import { useRouter } from "vue-router";
import { ComputedRef } from "vue";
import { phase } from "@/types/global";

const createConfirm = useConfirm();
const createSnackbar = useSnackbar();
const { t } = useTranslation();
const mainStore = useMainStore();

const dialog = ref({
  open: false,
  onSuccess: () => {}
});

const emit = defineEmits(["moduleUpdate"]);
const props = defineProps({
  current_phase: Object,
  current_phase_mode: String,
  next_phase: Object,
  save: Function,
  saving: Boolean,
  current_status: String,
  licenceId: Number,
  next_billing_cycle_anchor: Number
});

const model = defineModel<Record<number, number>>();
const mode = defineModel<string>("mode");

defineExpose({
  saveModules
});

const {
  headers,
  items,
  modulesRemoved,
  chargeNeeded,
  initialItems,
  loadPreview,
  changeNeeded,
  reviewData,
} = useModules(
  toRef(() => model.value),
  true,
  toRef(() => props.current_status),
  toRef(() => props.licenceId),
  toRef(() => mode.value),
  toRef(() => props.current_phase) as Ref<phase>,
  toRef(() => props.next_phase) as Ref<phase>,
  props.current_phase_mode,
  props.next_billing_cycle_anchor as number
);

const tempResult: ComputedRef<number> = computed(() => {
  return Object.entries(model.value as Record<number, number>).reduce((result, [key, value]) => {
    return value ? result | Number(key) : result;
  }, 0);
});

const modulesRequirements = computed(() => {
  return Object.fromEntries(
    mainStore.modules.map((item) => [item.value, item.requires])
  );
});

function changeValue(item: number, value: number) {
  console.log(value);
  model.value![item] = value;
  if (value === 0) {
    console.log(item);
    for (const [id, requires] of Object.entries(modulesRequirements.value)) {
      if (item & (requires || 0)) {
        changeValue(id as unknown as number, 0);
      }
    }
  }
}

const router = useRouter();

function saveModules(callback: () => void) {
  if (!changeNeeded.value) {
    router.back();
    return;
  }
  dialog.value.onSuccess = callback;
  dialog.value.open = true;
  chargeNeeded.value > 0 && loadPreview();
}

function submit() {
  dialog.value.onSuccess();
}
</script>

<style lang="scss" scoped>
.paymentMode {
  border-radius: 30px;
  background-color: #eeeeee;
  padding: 3px;

  .modeButton {
    border-radius: 25px;
    padding: 7px 20px;
    margin: 1px 4px;
    color: #666666;
  }
  @keyframes onHover {
    0%   {color: #666666;}
    100%  {color: black;}
  }
  @keyframes onLeave {
    0%   {color: black;}
    100%  {color: #666666;}
  }
  .not-active {
    animation: onLeave 0.5s;

    &:hover {
      color: black;
      animation: onHover 0.5s;
    }
  }

  .active {
    background-color: white;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2), 0 3px 10px 0 rgba(0, 0, 0, 0.19);
    color: black;
  }
}
</style>
